import { useContext, useEffect, useRef } from 'react';
import { CloseRequestContext } from '../contexts/close-request-context';
import { registerCloseRequest } from '../services/close-request-coordinator';
import type { CloseRequestOptions } from '../types/close-request.type';

/**
 * Route platform close requests through a layer's existing Close action.
 * @param options - Visibility and current dismissal policy.
 * @returns Identity to provide to nested layers through CloseRequestContext.
 */
export function useCloseRequest({
  isOpen,
  onClose,
  dismissible = true,
  modal = false,
}: CloseRequestOptions): symbol {
  const id = useRef(Symbol('close-layer')).current;
  const parentId = useContext(CloseRequestContext);
  const policy = useRef({ onClose, dismissible });

  useEffect(() => {
    policy.current = { onClose, dismissible };
  }, [onClose, dismissible]);

  useEffect(() => {
    if (!isOpen) return;
    return registerCloseRequest({
      id,
      parentId,
      modal,
      requestClose: () => {
        if (policy.current.dismissible) policy.current.onClose();
      },
    });
  }, [id, isOpen, parentId, modal]);

  return id;
}
